import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { Topbar } from "@/components/layout/Topbar";
import { LeftRail } from "@/components/layout/LeftRail";
import { Toaster } from "@/components/ui/Toaster";
import { BackToTop } from "@/components/ui/BackToTop";
import { useBreakpoint } from "@/hooks/useBreakpoint";

interface AppShellProps {
  children: ReactNode;
}

const RAIL_WIDTH = 232;
// NOT `flow.rail.collapsed` — that key is already taken by LeftRail's
// per-workspace group collapse state.
const RAIL_HIDDEN_KEY = "flow.rail.hidden";

export function AppShell({ children }: AppShellProps) {
  const isDesktop = useBreakpoint("md");
  const mainRef = useRef<HTMLElement>(null);
  const [railHidden, setRailHidden] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(RAIL_HIDDEN_KEY) === "true";
  });

  useEffect(() => {
    window.localStorage.setItem(RAIL_HIDDEN_KEY, String(railHidden));
  }, [railHidden]);

  const toggleRail = useCallback(() => setRailHidden((v) => !v), []);

  if (!isDesktop) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width: "100%",
          background: "var(--bg)",
          color: "var(--ink-1)",
        }}
      >
        {children}
        <Toaster />
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        background: "var(--bg)",
        color: "var(--ink-1)",
        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
      }}
    >
      <Topbar railHidden={railHidden} onToggleRail={toggleRail} />
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* The rail stays MOUNTED and is clipped to 0 width rather than
            unmounted: its boards/workspaces queries keep their data, so
            reopening is instant instead of flashing a "Wird geladen …". The
            wrapper owns the width so LeftRail keeps its own fixed 232px and
            the contents slide rather than reflow. */}
        <div
          style={{
            // `display: flex` is load-bearing, not styling. LeftRail's <aside>
            // used to be a direct flex child of the row and stretched to full
            // height for free; behind this wrapper it became an ordinary block
            // and collapsed to its content, so the rail's background stopped
            // mid-column whenever the workspaces were collapsed. Making the
            // wrapper a flex container restores the stretch.
            display: "flex",
            flexShrink: 0,
            width: railHidden ? 0 : RAIL_WIDTH,
            overflow: "hidden",
            transition: "width .18s ease",
          }}
          // `inert` (React 19) takes the hidden rail out of the tab order and
          // the a11y tree — without it, Tab still walks every invisible nav
          // link inside a 0px-wide box.
          inert={railHidden}
        >
          <LeftRail />
        </div>
        <main
          ref={mainRef}
          style={{
            flex: 1,
            minWidth: 0,
            // The content area is the scroll container, so a long page scrolls
            // here while the Topbar + LeftRail stay fixed (was: the whole window
            // scrolled, dragging the sidebar with it).
            minHeight: 0,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </main>
      </div>
      <BackToTop containerRef={mainRef} />
      <Toaster />
    </div>
  );
}
